'use strict';

class CodeSampleFactory {

  /**
   * Creates a new sample factory for an endpoint
   *
   * @param {Endpoint} endpoint
   * @param {number}   [indentation]
   * @param {string}   [quoteCharacter]
   */
  constructor ( endpoint, indentation = 4, quoteCharacter = '"' ) {

    /**
     * @type {Endpoint}
     * @private
     */
    this._endpoint = endpoint;

    /**
     * @type {number}
     * @private
     */
    this._indentation = indentation;

    /**
     * @type {string}
     * @private
     */
    this._quoteCharacter = quoteCharacter;
  }

  /**
   * Builds the sample code
   *
   * @param   {string} language
   * @param   {Object} parameters
   * @param   {string} accessToken
   * @returns {string}
   */
  buildSample ( language, parameters, accessToken ) {
    const headers = {
      Authorization: `Bearer ${ accessToken }`,
    };

    switch ( language ) {
      case 'shell':
        return this.buildCurlSample(parameters, headers);

      case 'javascript':
        return this.buildJavaScriptSample(parameters, headers);

      case 'php':
        return this.buildPHPSample(parameters, headers);

      case 'python':
        return this.buildPythonSample(parameters, headers);

      default:
        throw new Error('Unsupported language: Cannot build code sample');
    }
  }

  buildCurlSample ( parameters, headers ) {
    const url          = this._getEndpointUrl(parameters);
    const headerFields = this._formatHeaders(headers, ( name, value ) => `-H "${ name }: ${ value }"`);

    let commandLine = 'curl ' + headerFields.join(' ');

    if ( this._isGetRequest() ) {
      commandLine += ` -X ${ this._method }`;
    }

    if ( this._hasBody() ) {
      const body = this._formatBody(
        parameters,
        ( name, value ) => this._indent(`"${ name }": ${ JSON.stringify(value) },`),
      );

      if ( body.length > 0 ) {
        commandLine += this._endpoint.upload ? `-d '@/path/to/file'` : ` -d '{ \\\n${ body.join(' \\\n') } \\\n}'`;
      }
    }

    return `${ commandLine } ${ url }`;
  }

  buildJavaScriptSample ( parameters, headers ) {
    const url         = this._getEndpointUrl(parameters);
    const headerLines = this._formatHeaders(
      headers,
      ( name, value ) =>
        `${ name.match(/^[a-z]+$/i) ? name : `${ this._quote(name) }` }: ${ this._quote(value) },`,
    );

    const lines = [
      `const response = await fetch(${ this._quote(url) }, { `,
    ];

    if ( !this._isGetRequest() ) {
      lines.push(this._indent(`method: ${ this._quote(this._method) },`));
    }

    if ( headerLines.length > 0 ) {
      lines.push(this._indent('headers: {'));
      headerLines.forEach(line => lines.push(this._indent(line, 2)));
      lines.push(this._indent('},'));
    }

    if ( this._hasBody() ) {
      const bodyLines = this._formatBody(
        parameters,
        ( name, value ) => `${ name }: ${ this._encode(value) }`,
      );

      if ( bodyLines.length > 0 ) {
        lines.push(this._indent('body: {'));
        bodyLines.forEach(line => lines.push(this._indent(line, 2)));
        lines.push(this._indent('}'));
      }
    }

    lines.push('});');

    return lines.join('\n');
  }

  buildPHPSample ( parameters, headers ) {
    const url         = this._getEndpointUrl(parameters);
    const headerLines = this._formatHeaders(
      headers,
      ( name, value ) => this._quote(`${ name }: ${ value }`) + ',',
    );

    const lines = [
      '// This requires the curl extension to be installed',
      `$ch = curl_init(${ this._quote(url) });`,
      'curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);',
    ];

    switch ( this._method ) {
      case 'POST':
        lines.push('curl_setopt($ch, CURLOPT_POST, true);');
        break;

      case 'DELETE':
      case 'PATCH':
      case 'HEAD':
      case 'PUT':
        lines.push(`curl_setopt($ch, CURLOPT_CUSTOMREQUEST, ${ this._quote(this._method) });`);
        break;

      case 'GET':
      default:
    }

    if ( headerLines.length > 0 ) {
      lines.push(`curl_setopt($ch, CURLOPT_HTTPHEADER, [`);
      headerLines.forEach(line => lines.push(this._indent(line)));
      lines.push(`]);`);
    }

    if ( this._hasBody() ) {
      const bodyLines = this._formatBody(
        parameters,
        ( name, value ) => `${ this._quote(name) } => ${ this._encode(value) },`);

      if ( bodyLines.length > 0 ) {
        lines.push('curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([');
        bodyLines.forEach(line => lines.push(this._indent(line)));
        lines.push(']));');
      }
    }

    lines.push('$response = curl_exec($ch);');
    lines.push('curl_close($ch);');

    return lines.join('\n');
  }

  buildPythonSample ( parameters, headers ) {
    const url         = this._getEndpointUrl(parameters);
    const headerLines = this._formatHeaders(
      headers,
      ( name, value ) => `${ this._quote(name) }: ${ this._encode(value) },`,
    );

    const lines = [
      '# This requires the requests library to be installed',
      'import requests',
      '',
    ];

    lines.push(`response = requests.${ this._method.toLowerCase() }(`);
    lines.push(this._indent(this._quote(url)));

    if ( headerLines.length > 0 ) {
      lines.push(this._indent('headers={'));
      headerLines.forEach(line => lines.push(this._indent(line, 2)));
      lines.push(this._indent('},'));
    }

    if ( this._hasBody() ) {
      const bodyLines = this._formatBody(
        parameters,
        ( name, value ) => `${ this._quote(name) }: ${ this._encode(value) },`);

      if ( bodyLines.length > 0 ) {
        lines.push(this._indent('data={'));
        bodyLines.forEach(line => lines.push(this._indent(line, 2)));
        lines.push(this._indent('}'));
      }
    }

    lines.push(')');

    return lines.join('\n');
  }

  /**
   * Retrieves the endpoint URL pre-filled with all current values
   *
   * @param   {Object} parameters
   * @returns {URL}
   * @private
   */
  _getEndpointUrl ( parameters ) {
    const uri = this._endpoint.renderUri(parameters);
    const url = new URL(uri, window.location.origin);

    Object.entries(parameters)
      .filter(( [ key ] ) => key in this._endpoint.queryParameters)
      .forEach(( [ key, value ] ) => url.searchParams.append(key, value));

    return url;
  }

  /**
   * Formats the body with a given formatter callback, inserting all current values
   *
   * @param   {Object}   parameters
   * @param   {function} formatter
   * @returns {string[]}
   */
  _formatBody ( parameters, formatter ) {
    if ( this._endpoint.upload ) {
      return [ '<BINARY DATA>' ];
    } else {
      console.log('no upload endpoint', this._endpoint);
    }

    return this._endpoint
      .bodyParameters
      .map(parameter => formatter(
        parameter.name,
        parameters.hasOwnProperty(parameter.name)
          ? parameters[ parameter.name ]
          : null,
      ));
  }

  _formatHeaders ( headers, formatter ) {
    if ( this._endpoint.upload ) {
      headers[ 'Content-Type' ] = '%file mime type%';
    }

    return Object
      .entries(Object.assign({}, CodeSampleFactory._defaultHeaders, headers))
      .map(( [ name, value ] ) => formatter(name, value));
  }

  /**
   * Indents a line with the configured indentation
   *
   * @param   {string} line  Text to indent
   * @param   {number} level Amount of times to indent. Defaults to 1
   * @returns {string}       Indented line
   * @private
   */
  _indent ( line, level = 1 ) {
    return ( ' '.repeat(this._indentation * level) + line );
  }

  /**
   * Quotes a value
   *
   * @param   {*}      value
   * @returns {string}
   * @private
   */
  _quote ( value ) {
    return this._quoteCharacter + value + this._quoteCharacter;
  }

  _encode ( value ) {
    const json = JSON.stringify(value);

    if ( this._quoteCharacter !== '"' ) {
      return json.replace(/"/g, this._quoteCharacter);
    }

    return json;
  }

  /**
   * Checks whether a HTTP method allows a body
   *
   * @returns {boolean}
   * @private
   */
  _hasBody () {
    return [ 'POST', 'PUT', 'PATCH' ].includes(this._method);
  }

  /**
   * Checks whether this a GET endpoint
   *
   * @returns {boolean}
   * @private
   */
  _isGetRequest () {
    return this._method === 'GET';
  }

  /**
   * Retrieves the request method
   *
   * @returns {string}
   * @private
   */
  get _method () {
    return this._endpoint.methods[ 0 ];
  }

  /**
   * Holds all default headers
   *
   * @returns {{Accept: string}}
   * @private
   */
  static get _defaultHeaders () {
    return {
      'Content-Type': 'application/vnd.messengerpeople.v1+json',
      'Accept': 'application/vnd.messengerpeople.v1+json',
    };
  }
}

export default CodeSampleFactory;
